import { Tooltip } from '@surecompanies/core_components'
import {
  BriefcaseBusiness,
  Heart,
  HeartHandshake,
  UserRound,
  UserRoundPlus,
  type LucideIcon,
} from 'lucide-react'
import React from 'react'
import {
  RELATIONSHIP_CONFIG,
  RELATIONSHIP_ICON_COLORS,
  type RelationshipIconKey,
} from '../constants/relationship-badge.constants'
import { mapRelationshipDisplayToBadge } from '../view-models/relationship-badge.mapper'

export type RelationshipIconVariant = 'circle'

export type RelationshipIconSize = 'sm' | 'md' | 'lg'

export interface RelationshipIconProps {
  relationship: string
  variant?: RelationshipIconVariant
  size?: RelationshipIconSize
}

const GLYPH_STROKE_WIDTH = 2.5

const relationshipGlyphMap: Record<RelationshipIconKey, LucideIcon> = {
  employee: BriefcaseBusiness,
  spouse: Heart,
  domesticPartner: HeartHandshake,
  child: UserRound,
  childOfDomesticPartner: UserRoundPlus,
}

const relationshipIconSizeMap = {
  sm: { icon: 16, glyph: 12 },
  md: { icon: 20, glyph: 12 },
  lg: { icon: 24, glyph: 12 },
} as const

function getRelationshipIconKey(relationship: string): RelationshipIconKey {
  const badge = mapRelationshipDisplayToBadge(relationship)

  if (!badge) {
    return 'employee'
  }

  return RELATIONSHIP_CONFIG[badge].iconKey
}

function getRelationshipLabel(relationship: string): string {
  const badge = mapRelationshipDisplayToBadge(relationship)

  if (!badge) {
    return relationship
  }

  return RELATIONSHIP_CONFIG[badge].label
}

function RelationshipIconCircle({
  relationship,
  size = 'md',
}: Omit<RelationshipIconProps, 'variant'>): React.ReactElement {
  const iconKey = getRelationshipIconKey(relationship)
  const label = getRelationshipLabel(relationship)
  const Glyph = relationshipGlyphMap[iconKey]

  const icon = (
    <span
      tabIndex={0}
      aria-label={label}
      style={{
        width: relationshipIconSizeMap[size].icon,
        height: relationshipIconSizeMap[size].icon,
        borderRadius: '50%',
        background: RELATIONSHIP_ICON_COLORS[iconKey],
        display: 'inline-grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      <Glyph
        size={relationshipIconSizeMap[size].glyph}
        strokeWidth={GLYPH_STROKE_WIDTH}
        color="white"
        aria-hidden
      />
    </span>
  )

  return (
    <Tooltip title={label} mouseEnterDelay={0.2} trigger={['hover', 'focus']}>
      {icon}
    </Tooltip>
  )
}

const relationshipIconVariantMap = {
  circle: RelationshipIconCircle,
} as const

export function RelationshipIcon({
  relationship,
  variant = 'circle',
  size = 'md',
}: RelationshipIconProps): React.ReactElement {
  const IconByVariant = relationshipIconVariantMap[variant]
  return <IconByVariant relationship={relationship} size={size} />
}
